const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

export const PROPOSED_PAYMENT_STATUS = 'PROPOSED';
export const PENDING_PAYMENT_STATUS = 'PENDING';

export const roundMoney = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

export const formatMoneyInput = (value) => {
  if (value === '' || value == null) return '';
  return roundMoney(value).toFixed(2);
};

export const buildProposalReference = (apInvoiceId) => `PROPOSAL-${apInvoiceId}-${Date.now()}`;

const normalizePaymentMethod = (method) => (method || 'ACH').trim().toUpperCase();

const getPaymentAmountFromEntry = ({ mapping, transaction }) => (
  roundMoney(mapping?.paidAmount ?? transaction?.paymentAmount)
);

export const isActiveProposal = (transaction) => (
  (transaction?.paymentStatus || '').toUpperCase() === PROPOSED_PAYMENT_STATUS
);

export const isPostedPayment = (transaction) => {
  const status = (transaction?.paymentStatus || '').toUpperCase();
  return status === PENDING_PAYMENT_STATUS || status === 'PAID' || status === 'COMPLETED';
};

export const fetchInvoicePaymentHistory = async (tenantId, apInvoiceId) => {
  const mappings = await apiFetch(
    `conductor/invoice-payment-mappings/by-ap-invoice?tenant_id=${tenantId}&ap_invoice_id=${apInvoiceId}`
  ).catch(() => []);

  const list = Array.isArray(mappings) ? mappings : [];
  const enriched = await Promise.all(list.map(async (mapping) => {
    if (!mapping.paymentId) return { mapping, transaction: null };
    try {
      const transaction = await apiFetch(`conductor/payment-transactions/${mapping.paymentId}`);
      return { mapping, transaction };
    } catch {
      return { mapping, transaction: null };
    }
  }));

  return enriched.sort((a, b) => {
    const dateA = new Date(a.transaction?.createdAt || a.mapping.createdAt || a.mapping.mappingDate || 0);
    const dateB = new Date(b.transaction?.createdAt || b.mapping.createdAt || b.mapping.mappingDate || 0);
    return dateA - dateB;
  });
};

export const sumProposedPayments = (history = []) => roundMoney(history
  .filter(({ transaction }) => isActiveProposal(transaction))
  .reduce((sum, entry) => sum + getPaymentAmountFromEntry(entry), 0));

export const getRemainingProposalBalance = (invoiceBalance, history = []) => (
  roundMoney(Math.max(0, roundMoney(invoiceBalance) - sumProposedPayments(history)))
);

const buildConfigFromHistory = (history, invoiceBalance) => {
  const proposedEntries = history
    .filter(({ transaction }) => isActiveProposal(transaction))
    .map(({ mapping, transaction }) => ({
      paymentId: transaction.paymentId,
      mappingId: mapping.mappingId,
      payAmount: getPaymentAmountFromEntry({ mapping, transaction }),
      balanceAmount: Number(mapping.balanceAmount) || 0,
      bankId: transaction.bankId || mapping.bankId || '',
      paymentMethod: transaction.paymentMethod || 'ACH',
      createdAt: transaction.createdAt,
    }));

  if (!proposedEntries.length) return null;

  const totalPayAmount = roundMoney(proposedEntries.reduce((sum, item) => sum + item.payAmount, 0));
  const latest = proposedEntries[proposedEntries.length - 1];
  const balance = roundMoney(invoiceBalance);

  return {
    payAmount: totalPayAmount,
    totalPayAmount,
    remainingBalance: roundMoney(Math.max(0, balance - totalPayAmount)),
    proposedPayments: proposedEntries,
    bankId: latest.bankId,
    paymentMethod: latest.paymentMethod,
    isPartial: totalPayAmount < balance - 0.001,
    saved: true,
  };
};

export const loadProposedPaymentConfigs = async (tenantId, invoiceIds = [], rows = []) => {
  if (!tenantId || !invoiceIds.length) return {};

  const balanceByInvoice = Object.fromEntries(rows.map((row) => [row.apInvoiceId, row.balance]));
  const configs = {};

  await Promise.all(invoiceIds.map(async (apInvoiceId) => {
    const history = await fetchInvoicePaymentHistory(tenantId, apInvoiceId);
    const config = buildConfigFromHistory(history, balanceByInvoice[apInvoiceId]);
    if (config) configs[apInvoiceId] = config;
  }));

  return configs;
};

export const enrichConfigsWithBalances = (configs, rows = []) => {
  const balanceByInvoice = Object.fromEntries(rows.map((row) => [row.apInvoiceId, row.balance]));
  const enriched = {};
  Object.entries(configs).forEach(([apInvoiceId, config]) => {
    const balance = balanceByInvoice[apInvoiceId];
    enriched[apInvoiceId] = {
      ...config,
      remainingBalance: roundMoney(Math.max(0, roundMoney(balance) - roundMoney(config.totalPayAmount))),
      isPartial: roundMoney(config.totalPayAmount) < roundMoney(balance) - 0.001,
    };
  });
  return enriched;
};

const isSameAsLatestProposal = (latestEntry, { payAmount, bankId, paymentMethod }) => (
  latestEntry
  && latestEntry.payAmount === Number(payAmount)
  && (latestEntry.bankId || '') === (bankId || '')
  && normalizePaymentMethod(latestEntry.paymentMethod) === normalizePaymentMethod(paymentMethod)
);

export const upsertProposedPayment = async ({
  tenantId,
  row,
  payAmount,
  bankId,
  paymentMethod,
  userId,
  isFullPayment = false,
}) => {
  const history = await fetchInvoicePaymentHistory(tenantId, row.apInvoiceId);
  const remainingAvailable = getRemainingProposalBalance(row.balance, history);
  const normalizedPayAmount = roundMoney(isFullPayment ? remainingAvailable : payAmount);

  if (normalizedPayAmount <= 0) {
    throw new Error('No remaining balance available to pay');
  }

  if (normalizedPayAmount > remainingAvailable + 0.001) {
    throw new Error(`Amount cannot exceed remaining balance (${remainingAvailable.toFixed(2)})`);
  }

  const proposedEntries = history.filter(({ transaction }) => isActiveProposal(transaction));
  const latestEntry = proposedEntries.length
    ? {
      payAmount: getPaymentAmountFromEntry(proposedEntries[proposedEntries.length - 1]),
      bankId: proposedEntries[proposedEntries.length - 1].transaction?.bankId || '',
      paymentMethod: proposedEntries[proposedEntries.length - 1].transaction?.paymentMethod || 'ACH',
      transaction: proposedEntries[proposedEntries.length - 1].transaction,
      mapping: proposedEntries[proposedEntries.length - 1].mapping,
    }
    : null;

  const today = new Date().toISOString().slice(0, 10);
  const projectedBalance = roundMoney(Math.max(0, remainingAvailable - normalizedPayAmount));

  if (isSameAsLatestProposal(latestEntry, { payAmount: normalizedPayAmount, bankId, paymentMethod })) {
    const updatedTransaction = await apiFetch(
      `conductor/payment-transactions/${latestEntry.transaction.paymentId}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          ...latestEntry.transaction,
          bankId: bankId || null,
          paymentAmount: normalizedPayAmount,
          paymentMethod,
          updatedBy: userId || null,
        }),
      }
    );

    const updatedMapping = await apiFetch(
      `conductor/invoice-payment-mappings/${latestEntry.mapping.mappingId}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          ...latestEntry.mapping,
          bankId: updatedTransaction.bankId || bankId || null,
          paidAmount: normalizedPayAmount,
          balanceAmount: projectedBalance,
          mappingDate: today,
          updatedBy: userId || null,
        }),
      }
    );

    const refreshedHistory = history.map((entry) => (
      entry.mapping.mappingId === latestEntry.mapping.mappingId
        ? { mapping: updatedMapping, transaction: updatedTransaction }
        : entry
    ));

    return buildConfigFromHistory(refreshedHistory, row.balance);
  }

  const transaction = await apiFetch('conductor/payment-transactions', {
    method: 'POST',
    body: JSON.stringify({
      tenantId,
      vendorId: row.vendorId || null,
      bankId: bankId || null,
      paymentReference: buildProposalReference(row.apInvoiceId),
      paymentAmount: normalizedPayAmount,
      paymentMethod,
      paymentStatus: PROPOSED_PAYMENT_STATUS,
      paymentBatchId: null,
      createdBy: userId || null,
    }),
  });

  const mapping = await apiFetch('conductor/invoice-payment-mappings', {
    method: 'POST',
    body: JSON.stringify({
      tenantId,
      apInvoiceId: row.apInvoiceId,
      paymentId: transaction.paymentId,
      bankId: bankId || null,
      paidAmount: normalizedPayAmount,
      balanceAmount: projectedBalance,
      mappingDate: today,
      createdBy: userId || null,
    }),
  });

  return buildConfigFromHistory([...history, { mapping, transaction }], row.balance);
};

export const getProposedPaymentsForBatch = (config) => {
  if (Array.isArray(config?.proposedPayments) && config.proposedPayments.length) {
    return config.proposedPayments;
  }
  if (config?.paymentId) {
    return [{
      paymentId: config.paymentId,
      mappingId: config.mappingId,
      payAmount: config.payAmount,
      bankId: config.bankId,
      paymentMethod: config.paymentMethod,
    }];
  }
  return [];
};

export const collectBatchPaymentLines = (selectedRows = [], paymentConfigs = {}) => {
  const lines = [];

  selectedRows.forEach((row) => {
    const proposals = getProposedPaymentsForBatch(paymentConfigs[row.apInvoiceId]);
    proposals.forEach((proposal, index) => {
      const amount = Number(proposal.payAmount) || 0;
      if (amount <= 0) return;

      lines.push({
        lineId: proposal.paymentId || `${row.apInvoiceId}-${index}`,
        apInvoiceId: row.apInvoiceId,
        invoiceNumber: row.invoiceNumber,
        vendorName: row.vendorName,
        vendorId: row.vendorId,
        bankId: proposal.bankId || '',
        paymentMethod: proposal.paymentMethod || 'ACH',
        amount,
        paymentId: proposal.paymentId,
        mappingId: proposal.mappingId,
        row,
        proposal,
      });
    });
  });

  return lines;
};

const finalizeBankGroup = (group) => {
  const methods = Array.from(group.paymentMethods).sort();
  return {
    ...group,
    paymentMethodLabel: methods.length === 1 ? methods[0] : methods.join(', '),
  };
};

export const groupBatchPaymentLinesByBank = (lines = []) => {
  const groups = new Map();

  lines.forEach((line) => {
    const key = line.bankId || '__unassigned__';
    const existing = groups.get(key);

    if (!existing) {
      groups.set(key, {
        groupId: key,
        bankId: line.bankId || '',
        amount: line.amount,
        paymentMethods: new Set([line.paymentMethod || 'ACH']),
        paymentCount: 1,
        lines: [line],
      });
      return;
    }

    existing.amount += line.amount;
    existing.paymentMethods.add(line.paymentMethod || 'ACH');
    existing.paymentCount += 1;
    existing.lines.push(line);
  });

  return Array.from(groups.values()).map(finalizeBankGroup);
};

export const groupBatchPaymentLinesByVendorAndBank = (lines = []) => {
  const vendorMap = new Map();

  lines.forEach((line) => {
    const vendorKey = line.vendorId || line.vendorName || '__unassigned_vendor__';
    if (!vendorMap.has(vendorKey)) {
      vendorMap.set(vendorKey, {
        vendorId: line.vendorId || '',
        vendorName: line.vendorName || '—',
        bankGroups: new Map(),
      });
    }

    const vendorGroup = vendorMap.get(vendorKey);
    const bankKey = line.bankId || '__unassigned__';
    const existing = vendorGroup.bankGroups.get(bankKey);

    if (!existing) {
      vendorGroup.bankGroups.set(bankKey, {
        groupId: `${vendorKey}::${bankKey}`,
        bankId: line.bankId || '',
        amount: line.amount,
        paymentMethods: new Set([line.paymentMethod || 'ACH']),
        paymentCount: 1,
        lines: [line],
      });
      return;
    }

    existing.amount += line.amount;
    existing.paymentMethods.add(line.paymentMethod || 'ACH');
    existing.paymentCount += 1;
    existing.lines.push(line);
  });

  return Array.from(vendorMap.values())
    .map((vendor) => {
      const bankGroups = Array.from(vendor.bankGroups.values()).map(finalizeBankGroup);
      return {
        vendorId: vendor.vendorId,
        vendorName: vendor.vendorName,
        bankGroups,
        amount: bankGroups.reduce((sum, group) => sum + group.amount, 0),
        paymentCount: bankGroups.reduce((sum, group) => sum + group.paymentCount, 0),
      };
    })
    .sort((a, b) => a.vendorName.localeCompare(b.vendorName));
};

export const getSelectedRowsMissingProposals = (selectedRows = [], paymentConfigs = {}) => (
  selectedRows.filter((row) => !getProposedPaymentsForBatch(paymentConfigs[row.apInvoiceId]).length)
);

export const buildBatchBanksPayload = (bankGroups = [], vendorBankGroups = []) => {
  const vendorsByBank = new Map();

  vendorBankGroups.forEach((vendor) => {
    vendor.bankGroups.forEach((bankGroup) => {
      const bankKey = bankGroup.bankId || '__unassigned__';
      if (!vendorsByBank.has(bankKey)) {
        vendorsByBank.set(bankKey, []);
      }
      vendorsByBank.get(bankKey).push({
        vendorId: vendor.vendorId || null,
        vendorName: vendor.vendorName,
        paymentMethods: bankGroup.paymentMethodLabel,
        paymentCount: bankGroup.paymentCount,
        totalAmount: bankGroup.amount,
      });
    });
  });

  vendorsByBank.forEach((vendors) => {
    vendors.sort((a, b) => (a.vendorName || '').localeCompare(b.vendorName || ''));
  });

  return bankGroups.map((group) => ({
    bankId: group.bankId || null,
    paymentMethods: group.paymentMethodLabel,
    paymentCount: group.paymentCount,
    totalAmount: group.amount,
    vendors: vendorsByBank.get(group.bankId || '__unassigned__') || [],
  }));
};

/**
 * Batch header payment_method is a single ACH|WIRE|CHECK value.
 * Mixed methods (e.g. ACH + WIRE on one bank) stay in batch_banks JSON only.
 */
export const resolveBatchHeaderPaymentMethod = (bankGroups = []) => {
  if (!Array.isArray(bankGroups) || bankGroups.length !== 1) return null;

  const group = bankGroups[0];
  let methods = [];
  if (group?.paymentMethods instanceof Set) {
    methods = Array.from(group.paymentMethods).map((m) => String(m || '').trim().toUpperCase()).filter(Boolean);
  } else if (group?.paymentMethodLabel) {
    methods = String(group.paymentMethodLabel)
      .split(',')
      .map((m) => m.trim().toUpperCase())
      .filter(Boolean);
  }

  const unique = [...new Set(methods)];
  if (unique.length === 1) return unique[0];
  return null;
};
